/**
 * Permits & NOC hub (prepare-to-attest).
 *
 * Lists permit applications (status badges) with a "Prepare application" button,
 * plus sections for expiring contractor licenses and Notices of Commencement.
 *
 * LEGAL / SAFETY (CRITICAL): this module is "prepare-to-attest". BEAM PREPARES and
 * TRACKS — it never auto-submits, auto-notarizes or auto-records. The owner submits
 * applications and notarizes/records NOCs themselves, elsewhere. Every action here
 * either prefills a draft or RECORDS that a human already performed the legal step.
 * Button labels and helper text make that explicit.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). No raw style values.
 */

import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPermitApplications,
  listExpiringLicenses,
  listNocs,
  markNocSigned,
  markNocRecorded,
  type PermitApplicationRow,
  type ContractorLicenseRow,
  type NocRow,
} from '../../src/api/permits';
import {
  Screen,
  Text,
  Badge,
  Card,
  Button,
  ListRow,
} from '../../src/components/ui';
import type { StatusKey } from '../../src/theme';
import { theme } from '../../src/theme';
import { extractErrorMessage, humanize, formatCurrency } from '../../src/lib/format';

// ---------------------------------------------------------------------------
// Local status → badge maps (statusMap.ts is a shared file we must not modify)
// ---------------------------------------------------------------------------

function permitBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'issued':
    case 'approved':
    case 'finalized':
    case 'closed':
      return { key: 'paid', label: humanize(status) };
    case 'submitted':
      return { key: 'sent', label: 'Submitted' };
    case 'prepared':
    case 'under_review':
    case 'in_review':
      return { key: 'inProgress', label: humanize(status) };
    case 'rejected':
    case 'denied':
    case 'expired':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Draft' };
  }
}

function nocBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'recorded':
      return { key: 'paid', label: 'Recorded' };
    case 'signed':
      return { key: 'inProgress', label: 'Signed' };
    case 'generated':
      return { key: 'sent', label: 'Generated' };
    default:
      return { key: 'draft', label: humanize(status) || 'Draft' };
  }
}

function CenteredLoader({ label }: { label: string }) {
  return (
    <View style={styles.centeredState}>
      <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel={label} />
      <Text variant="body" color="secondary">
        {label}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function PermitsScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const appsQuery = useQuery({
    queryKey: ['permit-applications'],
    queryFn: listPermitApplications,
    staleTime: 30_000,
  });
  const licensesQuery = useQuery({
    queryKey: ['permit-licenses-expiring'],
    queryFn: listExpiringLicenses,
    staleTime: 60_000,
  });
  const nocsQuery = useQuery({
    queryKey: ['nocs'],
    queryFn: listNocs,
    staleTime: 30_000,
  });

  const signMutation = useMutation({
    mutationFn: (id: string) => markNocSigned(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nocs'] }),
  });
  const recordMutation = useMutation({
    mutationFn: (id: string) => markNocRecorded(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nocs'] }),
  });

  if (appsQuery.isLoading) {
    return (
      <Screen>
        <CenteredLoader label="Loading permits…" />
      </Screen>
    );
  }

  const apps = appsQuery.data ?? [];
  const licenses = licensesQuery.data ?? [];
  const nocs = nocsQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Permits & NOC</Text>

      {/* Human-in-the-loop framing */}
      <Card>
        <Text variant="small" color="secondary">
          BEAM prepares and tracks your permit paperwork. It never files, submits,
          notarizes or records anything for you — you submit applications and
          notarize/record Notices of Commencement yourself. The buttons here prepare
          drafts or record that you already did the legal step.
        </Text>
      </Card>

      <Button
        title="New filing (capability tiers)"
        testID="permit-new-filing"
        onPress={() => router.push('/(app)/permits/new')}
        accessibilityLabel="Start a new tiered permit filing — builds the canonical envelope"
      />
      <Button
        title="Prepare application"
        variant="secondary"
        onPress={() => router.push('/(app)/permits/prepare')}
        accessibilityLabel="Prepare a new permit application draft"
      />
      <Button
        title="Prepare a Notice of Commencement"
        variant="secondary"
        onPress={() => router.push('/(app)/permits/noc')}
        accessibilityLabel="Prepare a Notice of Commencement draft"
      />
      <Button
        title="Manage jurisdictions & permit types"
        variant="secondary"
        onPress={() => router.push('/(app)/permits/manage')}
        accessibilityLabel="Add jurisdictions and permit types"
      />

      {/* ------------------------------------------------------------------ */}
      {/* Permit applications */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Permit applications
        </Text>
        {appsQuery.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(appsQuery.error, 'Failed to load permit applications.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => appsQuery.refetch()} />
          </Card>
        ) : apps.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No permit applications yet. Prepare one to get started — you’ll review
              and submit it yourself in the jurisdiction’s portal.
            </Text>
          </Card>
        ) : (
          <View style={styles.list} testID="permit-list">
            {apps.map((app: PermitApplicationRow, i: number) => {
              const b = permitBadge(app.status);
              const subtitle = [
                app.permit_number ? `#${app.permit_number}` : null,
                app.project_address ?? app.scope_of_work ?? null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <View key={app.id} testID={`permit-list-item-${i}`}>
                  <ListRow
                    title={app.applicant_name || app.scope_of_work || app.id.slice(0, 8).toUpperCase()}
                    subtitle={subtitle || humanize(app.status)}
                    right={<Badge status={b.key} label={b.label} />}
                    onPress={() => router.push(`/(app)/permits/${app.id}`)}
                  />
                </View>
              );
            })}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Expiring licenses */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Licenses expiring soon
        </Text>
        {licensesQuery.isLoading ? (
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading licenses" />
        ) : licensesQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(licensesQuery.error, 'Failed to load licenses.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => licensesQuery.refetch()} />
          </Card>
        ) : licenses.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No licenses expiring soon.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {licenses.map((lic: ContractorLicenseRow) => (
              <ListRow
                key={lic.id}
                title={lic.license_number}
                subtitle={[
                  lic.classification ? humanize(lic.classification) : null,
                  lic.expires_at ? `Expires ${new Date(lic.expires_at).toLocaleDateString()}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                right={<Badge status="overdue" label="Renew" />}
              />
            ))}
          </View>
        )}
      </View>

      {/* ------------------------------------------------------------------ */}
      {/* Notices of Commencement */}
      {/* ------------------------------------------------------------------ */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Notices of Commencement
        </Text>
        {nocsQuery.isLoading ? (
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading NOCs" />
        ) : nocsQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(nocsQuery.error, 'Failed to load NOCs.')}
            </Text>
            <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => nocsQuery.refetch()} />
          </Card>
        ) : nocs.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No Notices of Commencement yet. Generate one as a draft from a job, or
              use “Prepare a Notice of Commencement” above to enter the details
              manually. Either way you must sign/notarize and record it yourself.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {nocs.map((noc: NocRow) => {
              const b = nocBadge(noc.status);
              const busy = signMutation.isPending || recordMutation.isPending;
              const canSign = noc.status === 'generated' || noc.status === 'draft';
              const canRecord = noc.status === 'signed';
              return (
                <Card key={noc.id}>
                  <View style={styles.cardHeader}>
                    <Text variant="bodyStrong" numberOfLines={1} style={styles.flex1}>
                      {noc.property_address || noc.owner_name || noc.id.slice(0, 8).toUpperCase()}
                    </Text>
                    <Badge status={b.key} label={b.label} />
                  </View>
                  {noc.owner_name ? (
                    <Text variant="small" color="secondary">
                      Owner: {noc.owner_name}
                    </Text>
                  ) : null}

                  {/* Legal warning carried on the document — surfaced, never implying BEAM filed it. */}
                  {noc.legal_warning ? (
                    <View style={styles.warningBox}>
                      <Text variant="label" color="danger">
                        Legal warning
                      </Text>
                      <Text variant="small" color="secondary">
                        {noc.legal_warning}
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.warningBox}>
                      <Text variant="small" color="secondary">
                        This NOC is a draft prepared by BEAM. BEAM has NOT filed, notarized
                        or recorded it. Review the document, sign/notarize it, and record it
                        with the clerk yourself before relying on it.
                      </Text>
                    </View>
                  )}

                  {noc.recording_reference ? (
                    <Text variant="small" color="secondary">
                      Recording reference: {noc.recording_reference}
                    </Text>
                  ) : null}

                  <View style={styles.nocActions}>
                    {canSign ? (
                      <Button
                        title="Mark signed (I signed/notarized it)"
                        variant="secondary"
                        size="sm"
                        loading={signMutation.isPending}
                        disabled={busy}
                        onPress={() => signMutation.mutate(noc.id)}
                        accessibilityLabel="Record that you signed and notarized this NOC yourself"
                      />
                    ) : null}
                    {canRecord ? (
                      <Button
                        title="Mark recorded (I recorded it)"
                        variant="secondary"
                        size="sm"
                        loading={recordMutation.isPending}
                        disabled={busy}
                        onPress={() => recordMutation.mutate(noc.id)}
                        accessibilityLabel="Record that you recorded this NOC with the clerk yourself"
                      />
                    ) : null}
                  </View>
                  <Text variant="small" color="secondary">
                    These buttons only record that you performed the action — BEAM does
                    not sign, notarize or record on your behalf.
                  </Text>
                </Card>
              );
            })}
          </View>
        )}
        {signMutation.isError || recordMutation.isError ? (
          <Card>
            <Text variant="small" color="danger" accessibilityRole="alert">
              {extractErrorMessage(signMutation.error ?? recordMutation.error, 'Could not update the NOC.')}
            </Text>
          </Card>
        ) : null}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  flex1: { flex: 1 },
  warningBox: {
    gap: theme.spacing.xs,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    backgroundColor: theme.color.bg.subtle,
  },
  nocActions: { gap: theme.spacing.sm },
});
